import {
  resendClientEmail,
  sendClientOnboardEmail,
} from "@/store/slices/clientSlice";
// merge svg imports to avoid duplication
import {
  EditSVG,
  InputSearchIconSVG,
  PlusIconSVG,
  EmailSvg,
  ResendEmailSvg,
} from "@/assets/svg";
import {
  getOnboardEmailViewService,
  getResendTemplateService,
} from "@/service/client.service";
import { FilterSelectWithRadio } from "@/components/form";
import AppLayout from "@/components/layout/AppLayout";
import Modal from "@/components/Modal";
import NoDataContainer from "@/components/NoDataContainer";
import { useAppDispatch, useAppSelector } from "@/hooks";
import {
  addUser,
  fetchRoleOptions,
  fetchUsers,
  getUserDetail,
  resetFilters,
  selectFilteredUsers,
  selectNoUsers,
  selectPagination,
  selectRoleFilter,
  selectRoleOptions,
  selectSearchTerm,
  selectStatusFilter,
  setRoleFilter,
  setSearchTerm,
  setStatusFilter,
  updateUser,
} from "@/store/slices/userSlice";
import { User, UserQuery } from "@/types/user.model";
import { capitalizeFirstLetter } from "@/utils/constants";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import debounce from "lodash/debounce";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SendEmployeeEmailForm from "./components/SendEmployeeEmailForm";
import ResendFailedUserOnboardEmailForm, { ResendUserOnboardEmailData } from "./components/ResendFailedUserOnboardEmailForm";
import NewUserForm from "./components/NewUserForm";
import Tooltip from "@/components/Tooltip";

// Define column meta type for TypeScript
type ColumnMeta = {
  isSticky?: boolean;
  width?: string;
  maxWidth?: string;
};

// Status badge component
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  return (
    <span
      className={`px-2 py-1 rounded-sm text-xs font-medium ${
        status === "active"
          ? "bg-[#EDFFF9] text-[#0C7453]"
          : status === "inactive"
          ? "bg-[#FFEDED] text-[#FF0000]"
          : "bg-[#FFF8ED] text-[#D88C0C]"
      }`}
    >
      {capitalizeFirstLetter(status)}
    </span>
  );
};

// User table component using TanStack React Table
const UserTable: React.FC<{
  data: User[];
  onEdit: (user: User) => void;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
  onPageChange: (page: number) => void;
  onViewEmail: (
    emailId: string,
    status: string,
    employeeName: string,
    roleName: string,
    userId: string
  ) => void;
  onResendEmail: (
    userId: string,
    employeeName: string,
    roleName: string,
    email: string
  ) => void;
}> = ({ data, onEdit, pagination, onPageChange, onViewEmail, onResendEmail }) => {
  const columnHelper = createColumnHelper<User>();

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => `${row.first_name} ${row.last_name}`, {
        id: "name",
        header: "Employee/contractor name",
        cell: (info) => (
          <div className="text-wrap" title={info.getValue()}>
            {info.getValue()}
          </div>
        ),
        meta: {
          width: "225px",
          maxWidth: "225px",
        } as ColumnMeta,
      }),
      columnHelper.accessor((row) => row.role?.name, {
        id: "role",
        header: "Role",
        cell: (info) => {
          return (
            <div className="text-wrap">
              {capitalizeFirstLetter(info.row.original.role?.name as string)}
            </div>
          );
        },
        meta: { width: "80px" } as ColumnMeta,
      }),
      columnHelper.accessor("email", {
        header: "Email address",
        cell: (info) => (
          <div
            className="text"
            title={info.getValue()}
            style={{ overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}
          >
            {info.getValue()}
          </div>
        ),
        meta: { width: "276px" } as ColumnMeta,
      }),
      columnHelper.accessor("contact_number", {
        header: "Phone",
        cell: (info) => info.getValue(),
        meta: { width: "160px" } as ColumnMeta,
      }),
      columnHelper.accessor("status", {
        header: "Status",
        cell: (info) => <StatusBadge status={info.getValue() as string} />,
        meta: { width: "120px" } as ColumnMeta,
      }),
      columnHelper.display({
        id: "actions",
        header: "Action",
        cell: (info) => {
          const showEmailButton = info.row.original.onboard_email_details;
          const emailSvgColor = showEmailButton
            ? showEmailButton.status === "sent"
              ? "#059669"
              : showEmailButton.status === "pending"
              ? "#F59E0B"
              : "#DC2626"
            : "#DC2626";
          const employeeName = `${info.row.original.first_name || ""} ${
            info.row.original.last_name || ""
          }`.trim();
          const roleName = info.row.original.role?.name || "";
          return (
            <div className="flex gap-2 items-center justify-start">
              {info.row.original.role?.name !== "ADMIN" && (
                <Tooltip content="Edit">
                  <button
                    className="cursor-pointer"
                    onClick={() => onEdit(info.row.original)}
                  >
                    <EditSVG />
                  </button>
                </Tooltip>
              )}
              {showEmailButton && (
                <Tooltip content="Send email">
                  <button
                    onClick={() =>
                      onViewEmail(
                        showEmailButton.id,
                        showEmailButton.status,
                        employeeName,
                        roleName,
                        info.row.original.id as string
                      )
                    }
                    className="cursor-pointer"
                  >
                    <EmailSvg color={emailSvgColor} />
                  </button>
                </Tooltip>
              )}
              {showEmailButton?.is_resend_available && (
                <Tooltip content="Resend email">
                  <button
                    onClick={() =>
                      onResendEmail(
                        info.row.original.id as string,
                        employeeName,
                        roleName,
                        info.row.original.email
                      )
                    }
                    className="cursor-pointer"
                  >
                    <ResendEmailSvg color="#73B5C5" />
                  </button>
                </Tooltip>
              )}
            </div>
          );
        },
        meta: { width: "140px" } as ColumnMeta,
      }),
    ],
    [columnHelper, onEdit, onResendEmail]
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // We're handling pagination at the API level, so we don't need the pagination model
    // getPaginationRowModel: getPaginationRowModel(),
    // initialState: {
    //   pagination: {
    //     pageSize: 10,
    //   },
    // },
  });

  return (
    <>
      <div className="overflow-x-auto relative py-4">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-[#F4F7F9] text-left">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isSticky = (header.column.columnDef.meta as any)
                    ?.isSticky;
                  return (
                    <th
                      key={header.id}
                      scope="col"
                      className={`p-3 text-sm font-[600] text-[#1E1E1E] tracking-wider ${
                        isSticky
                          ? "sticky right-0 bg-[#F4F7F9] z-10 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]"
                          : ""
                      }`}
                      style={{
                        width: (header.column.columnDef.meta as ColumnMeta)
                          ?.width,
                        minWidth: (header.column.columnDef.meta as ColumnMeta)
                          ?.width,
                        maxWidth: (header.column.columnDef.meta as ColumnMeta)
                          ?.maxWidth,
                      }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 border-l-1 border-r-1 border-b-1 border-gray-200">
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  No users found
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-[#F9FEFF]">
                  {row.getVisibleCells().map((cell) => {
                    const isSticky = (cell.column.columnDef.meta as any)
                      ?.isSticky;
                    return (
                      <td
                        key={cell.id}
                        className={`p-3 whitespace-nowrap text-sm text-[#1E1E1E] font-[500] ${
                          isSticky ? "sticky right-0 bg-white" : ""
                        }`}
                        style={{
                          width: (cell.column.columnDef.meta as ColumnMeta)
                            ?.width,
                          minWidth: (cell.column.columnDef.meta as ColumnMeta)
                            ?.width,
                          maxWidth: (cell.column.columnDef.meta as ColumnMeta)
                            ?.maxWidth,
                        }}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="px-4 py-4 text-sm text-[#1E1E1E] font-[600] flex justify-between items-center">
        <div>
          Showing{" "}
          {pagination.currentPage === 1
            ? 1
            : (pagination.currentPage - 1) * pagination.pageSize + 1}{" "}
          to{" "}
          {Math.min(
            pagination.currentPage * pagination.pageSize,
            pagination.totalItems
          )}{" "}
          of {pagination.totalItems} results
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={() => {
              if (pagination.currentPage > 1) {
                onPageChange(pagination.currentPage - 1);
              }
            }}
            disabled={pagination.currentPage <= 1}
            className="px-3 py-1 w-[78px] h-[34px] text-sm text-[#1E1E1E] bg-[#F5F5F5] rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            Previous
          </button>
          <button
            onClick={() => {
              if (pagination.currentPage < pagination.totalPages) {
                onPageChange(pagination.currentPage + 1);
              }
            }}
            disabled={pagination.currentPage >= pagination.totalPages}
            className="px-3 py-1 w-[78px] h-[34px] text-sm text-white bg-[#73B5C5] rounded-md hover:bg-[#5da7b8] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
};

// User interface is now imported from userSlice

const UserManagement: React.FC = () => {
  // We'll use auth state later when implementing permission checks
  // const { user: currentUser } = useAppSelector((state) => state.auth);

  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isResendEmailModalOpen, setIsResendEmailModalOpen] = useState(false);
  const [isResendFailedOpen, setIsResendFailedOpen] = useState(false);
  const [resendFailedData, setResendFailedData] = useState<ResendUserOnboardEmailData | null>(null);
  const [resendEmailMeta, setResendEmailMeta] = useState<{
    userId: string;
    employeeName: string;
    roleName: string;
    to: string;
    cc: string;
    bcc: string;
    subject: string;
    content: string;
  }>({
    userId: "",
    employeeName: "",
    roleName: "",
    to: "",
    cc: "",
    bcc: "",
    subject: "",
    content: "",
  });
  const [emailMeta, setEmailMeta] = useState<{
    userId?: string;
    employeeName: string;
    roleName: string;
    to: string;
    cc?: string;
    bcc?: string;
    subject?: string;
    content?: string;
    sentDate?: string;
    sentTime?: string;
    viewMode?: boolean;
    emailStatus?: string;
  }>({
    userId: undefined,
    employeeName: "",
    roleName: "",
    to: "",
    cc: "",
    bcc: "",
    subject: "",
    content: "",
    sentDate: "",
    sentTime: "",
    viewMode: false,
    emailStatus: undefined,
  });
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [modalMode, setModalMode] = useState<"add" | "edit">("add");

  // Get user data from Redux store
  const filteredUsers = useAppSelector(selectFilteredUsers);
  const searchTerm = useAppSelector(selectSearchTerm);
  const roleFilter = useAppSelector(selectRoleFilter);
  const statusFilter = useAppSelector(selectStatusFilter);
  const pagination = useAppSelector(selectPagination);
  const roleOptions = useAppSelector(selectRoleOptions);
  const noUsers = useAppSelector(selectNoUsers);

  // Track the currently applied filter values for reset functionality
  const [currentRoleFilter, setCurrentRoleFilter] = useState("All roles");
  const [currentStatusFilter, setCurrentStatusFilter] =
    useState("All statuses");

  // Update filter state and trigger API call when filters change
  useEffect(() => {
    // Only trigger API call if the component has mounted (prevents double API call on init)
    if (initialDataLoaded.current) {
      setCurrentRoleFilter(roleFilter);
      setCurrentStatusFilter(statusFilter);

      dispatch(
        fetchUsers({
          page: 1, // Reset to first page when applying filters
          pageSize: pagination.pageSize,
          search: searchTerm || undefined,
          role: roleFilter !== "All roles" ? roleFilter : undefined,
          status:
            statusFilter !== "All statuses"
              ? statusFilter.toLowerCase()
              : undefined,
        })
      );
    }
  }, [roleFilter, statusFilter, dispatch, pagination.pageSize]);

  const handleOpenModal = () => {
    setModalMode("add");
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const handleEditUser = (user: User) => {
    setModalMode("edit");

    // Fetch the latest user details from the API instead of using table data
    dispatch(getUserDetail(user.id!))
      .unwrap()
      .then((userData: User) => {
        setEditingUser(userData);
        setIsModalOpen(true);
      })
      .catch((error: Error) => {
        console.error("Failed to fetch user details:", error);
        // Fallback to using the table data if API call fails
        setEditingUser(user);
        setIsModalOpen(true);
      });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  // Use ref to track if initial data has been loaded
  const initialDataLoaded = useRef(false);

  // Create a debounced search function - only for search, not for role/status filters
  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      dispatch(
        fetchUsers({
          page: 1, // Reset to first page when searching
          pageSize: pagination.pageSize,
          search: searchValue || undefined,
          role: roleFilter !== "All roles" ? roleFilter : undefined,
          status:
            statusFilter !== "All statuses"
              ? statusFilter.toLowerCase()
              : undefined,
        })
      );
    }, 500),
    [dispatch, pagination.pageSize, roleFilter, statusFilter]
  );

  // Handle search term changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    dispatch(setSearchTerm(value));
    debouncedSearch(value);
  };

  // Fetch users and roles on component mount (only once)
  useEffect(() => {
    if (!initialDataLoaded.current) {
      const initialQuery: UserQuery = {
        page: 1,
        pageSize: 20,
      };
      dispatch(fetchUsers(initialQuery));
      dispatch(fetchRoleOptions());
      initialDataLoaded.current = true;
    }

    // Reset filters when component unmounts
    return () => {
      dispatch(resetFilters());
    };
  }, [dispatch]);

  // Filtering is now handled in the Redux slice

  const handleAddUser = (userData: User) => {
    if (modalMode === "edit" && editingUser) {
      // Update existing user
      const updatedUser: User = {
        ...editingUser,
        first_name: userData.first_name,
        last_name: userData.last_name,
        role_id: userData.role_id,
        status:
          (userData.status?.toLowerCase() as
            | "active"
            | "inactive"
            | "pending") || editingUser.status,
        email: userData.email,
        contact_number: userData.contact_number,
      };

      // Dispatch update user action and close modal only after success
      dispatch(updateUser(updatedUser))
        .unwrap()
        .then(() => {
          setIsModalOpen(false);
          setEditingUser(null);
        })
        .catch((error) => {
          console.error("Failed to update user:", error);
          // Modal stays open on error
        });
    } else {
      // Create a new user object
      const newUser: User = {
        id: Date.now().toString(),
        first_name: userData.first_name,
        last_name: userData.last_name,
        role_id: userData.role_id,
        email: userData.email,
        contact_number: userData.contact_number,
      };

      // Dispatch the addUser action and close modal only after success
      dispatch(addUser(newUser))
        .unwrap()
        .then((created) => {
          setIsModalOpen(false);
          setEditingUser(null);
          const createdUser = created as User;
          const roleName =
            roleOptions.find((r) => r.id === createdUser.role_id)?.name || "";
                setEmailMeta({
            userId: createdUser.id,
            employeeName: `${createdUser.first_name || ""} ${createdUser.last_name || ""}`.trim(),
            roleName,
            to: createdUser.email,
            emailStatus: "",
            subject: createdUser?.email_prefill?.subject || "",
            content: createdUser?.email_prefill?.body || "",
            viewMode: false,
          });
          
          setIsEmailModalOpen(true);
        })
        .catch((error) => {
          console.error("Failed to add user:", error);
          // Modal stays open on error
        });
    }
  };

  type EmailFormPayload = {
    to: string[];
    cc: string[];
    bcc: string[];
    subject: string;
    content: string;
  };

  const handleSendLater = (p: EmailFormPayload) => {
    if (!emailMeta.userId) return;
    dispatch(
      // @ts-ignore thunk from client slice reused
      sendClientOnboardEmail({
        clientId: emailMeta.userId,
        payload: {
          action: "send_later",
          to: p.to,
          cc: p.cc,
          bcc: p.bcc,
          subject: p.subject,
          body: p.content,
        },
      })
    )
      // @ts-ignore unwrap
      .unwrap()
      .then(() => {
        setIsEmailModalOpen(false);
        // refresh users list
        dispatch(
          fetchUsers({
            page: pagination.currentPage,
            pageSize: pagination.pageSize,
          } as any as UserQuery)
        );
      });
  };

  const handleSendNow = (p: EmailFormPayload) => {
    if (!emailMeta.userId) return;
    dispatch(
      // @ts-ignore thunk from client slice reused
      sendClientOnboardEmail({
        clientId: emailMeta.userId,
        payload: {
          action: "send_now",
          to: p.to,
          cc: p.cc,
          bcc: p.bcc,
          subject: p.subject,
          body: p.content,
        },
      })
    )
      // @ts-ignore unwrap
      .unwrap()
      .then(() => {
        setIsEmailModalOpen(false);
        // refresh users list
        dispatch(
          fetchUsers({
            page: pagination.currentPage,
            pageSize: pagination.pageSize,
          } as any as UserQuery)
        );
      });
  };

  const onResendEmail = async (
    userId: string,
    employeeName: string,
    roleName: string,
    email: string
  ) => {
    try {
      const { data } = await getResendTemplateService(userId);
      const payload = data?.data || {};
      const ccArr: string[] = payload.cc || [];
      const bccArr: string[] = payload.bcc || [];
      const subject: string = payload.subject || "";
      const body: string = payload.body || payload.html || "";

      setResendEmailMeta({
        userId,
        employeeName,
        roleName,
        to: email,
        cc: ccArr.join(", "),
        bcc: bccArr.join(", "),
        subject,
        content: body,
      });
      setIsResendEmailModalOpen(true);
    } catch (e) {
      console.error("Failed to fetch resend template", e);
    }
  };

  const handleResendSendNow = (p: EmailFormPayload) => {
    if (!resendEmailMeta.userId) return;
    dispatch(
      // @ts-ignore thunk
      resendClientEmail({
        userId: resendEmailMeta.userId,
        payload: {
          action: "send_now",
          to: p.to,
          cc: p.cc,
          bcc: p.bcc,
          subject: p.subject,
          body: p.content,
        },
      })
    )
      // @ts-ignore unwrap
      .unwrap()
      .then(() => {
        setIsResendEmailModalOpen(false);
        dispatch(
          fetchUsers({
            page: pagination.currentPage,
            pageSize: pagination.pageSize,
          } as UserQuery)
        );
      });
  };

  // Open email modal for existing onboarding emails
  const onViewEmail = async (
    emailId: string,
    status: string,
    employeeName: string,
    roleName: string,
    userId: string
  ) => {
    try {
      const { data } = await getOnboardEmailViewService(emailId);
      const payload = data?.data || {};
      const normStatus = (status || "").toLowerCase();
      const toArr: string[] = payload.to || [];
      const ccArr: string[] = payload.cc || [];
      const bccArr: string[] = payload.bcc || [];
      const subject: string = payload.subject || "";
      const body: string = payload.body || payload.html || "";
      const sentAt: string | undefined = payload.sent_at || payload.sentAt;
      let sentDate = "";
      let sentTime = "";
      if (sentAt) {
        const d = new Date(sentAt);
        if (!Number.isNaN(d.getTime())) {
          sentDate = d.toLocaleDateString();
          sentTime = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        }
      } else {
        sentDate = payload.send_date || "";
        sentTime = payload.send_time || "";
      }
      if (normStatus === "failed") {
        const reason: string | null = payload.error || payload.reason || null;
        const orgName: string | null =
          payload?.owner_details?.client?.org_name ||
          payload?.org_name ||
          null;
        setResendFailedData({
          reason,
          employeeName,
          orgName,
          to: toArr,
          cc: ccArr,
          bcc: bccArr,
          subject,
          bodyHtml: body,
        });
        setEmailMeta((prev) => ({ ...prev, userId }));
        setIsResendFailedOpen(true);
      } else {
        setEmailMeta((prev) => ({
          ...prev,
          userId,
          employeeName,
          roleName,
          to: toArr.join(", "),
          cc: ccArr.join(", "),
          bcc: bccArr.join(", "),
          subject,
          content: body,
          sentDate,
          sentTime,
          viewMode: normStatus === "sent",
          emailStatus: normStatus,
        }));
        setIsEmailModalOpen(true);
      }
    } catch (e) {
      console.error("Failed to fetch onboarding email details", e);
    }
  };

  // Reset all filters and fetch users with no filters
  const handleResetFilter = () => {
    // Reset all filters
    dispatch(resetFilters());

    // Reset current filters to default values
    setCurrentRoleFilter("All roles");
    setCurrentStatusFilter("All statuses");

    dispatch(
      fetchUsers({
        page: 1,
        pageSize: pagination.pageSize,
      })
    );
  };

  return (
    <AppLayout
      title="Employee management"
      contentClassName={`min-h-[90%] ${
        pagination.totalPages ? "" : "flex items-center justify-center"
      }`}
      rightActions={
        <button
          onClick={handleOpenModal}
          className="bg-[#73B5C5] hover:bg-[#5da7b8] text-white px-4 py-2 rounded-md text-sm flex items-center cursor-pointer gap-1"
        >
          Add new employee
          <PlusIconSVG />
        </button>
      }
    >
      <div className="p-4">
        {noUsers ? (
          <NoDataContainer
            title="No users found"
            description="There are no users in the system yet. Add a new user to get started."
            buttonLabel="Add new user"
            onButtonClick={handleOpenModal}
          />
        ) : (
          <div className="bg-white rounded-lg">
            {/* Filters */}
            <div className="py-4 flex flex-wrap items-center justify-between gap-2">
              <div className="relative flex-grow max-w-[246px]">
                <input
                  type="text"
                  placeholder="Search"
                  className="w-full h-[42px] px-3 pl-9 bg-[#F9FBFF] rounded-[6px] focus:outline-none focus:border-[#73B5C5] placeholder:text-[#B5B7C0]"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                  <InputSearchIconSVG />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="flex gap-3 mr-3">
                  <div className="w-[142px]">
                    <FilterSelectWithRadio
                      options={[
                        { value: "All roles", label: "All roles" },
                        ...roleOptions.map((role) => ({
                          value: role.id,
                          label: capitalizeFirstLetter(role.name),
                        })),
                      ]}
                      value={roleFilter}
                      onChange={(value) => dispatch(setRoleFilter(value))}
                    />
                  </div>

                  <div className="w-[143px]">
                    <FilterSelectWithRadio
                      options={[
                        { value: "All statuses", label: "All statuses" },
                        { value: "Active", label: "Active" },
                        { value: "Inactive", label: "Inactive" },
                        // { value: "Pending", label: "Pending" },
                      ]}
                      value={statusFilter}
                      onChange={(value) => dispatch(setStatusFilter(value))}
                    />
                  </div>
                </div>

                <button
                  className={`h-[38px] px-4 text-sm text-[#6C6C6C] bg-white rounded-[6px] ${
                    currentRoleFilter !== "All roles" ||
                    currentStatusFilter !== "All statuses"
                      ? "hover:bg-gray-50 cursor-pointer"
                      : "opacity-50 cursor-not-allowed"
                  }`}
                  onClick={handleResetFilter}
                  disabled={
                    currentRoleFilter === "All roles" &&
                    currentStatusFilter === "All statuses"
                  }
                >
                  Reset filter
                </button>
              </div>
            </div>

            {/* Table with TanStack React Table */}
            <UserTable
              data={filteredUsers}
              onEdit={handleEditUser}
              pagination={pagination}
              onViewEmail={onViewEmail}
              onResendEmail={onResendEmail}
              onPageChange={(page) => {
                dispatch(
                  fetchUsers({
                    page,
                    pageSize: pagination.pageSize,
                  })
                );
              }}
            />
          </div>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={modalMode === "add" ? "Add employee" : "Edit employee"}
        width="min(95%, 868px)"
        contentClassName="p-0"
      >
        <NewUserForm
          onSubmit={handleAddUser}
          onCancel={handleCloseModal}
          initialData={
            editingUser
              ? {
                  first_name: editingUser.first_name || "",
                  last_name: editingUser.last_name || "",
                  email: editingUser.email,
                  contact_number: editingUser.contact_number,
                  role_id: editingUser.role?.id || "",
                  status: editingUser.status || "active",
                }
              : undefined
          }
          isEditMode={modalMode === "edit"}
        />
      </Modal>

      {/* Onboarding Email Modal (scaffold) */}
      <Modal
        isOpen={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        title={emailMeta.viewMode ? "View email" : "Send email"}
        width="min(95%, 868px)"
        contentClassName="p-0 max-h-[calc(100vh-100px)] overflow-y-auto"
      >
        <SendEmployeeEmailForm
          employeeName={emailMeta.employeeName}
          roleName={emailMeta.roleName}
          initialTo={emailMeta.to}
          initialCc={emailMeta.cc}
          initialBcc={emailMeta.bcc}
          initialSubject={emailMeta.subject}
          initialContent={emailMeta.content}
          sentDate={emailMeta.sentDate}
          sentTime={emailMeta.sentTime}
          viewMode={emailMeta.viewMode}
          emailStatus={emailMeta.emailStatus}
          onCancel={() => setIsEmailModalOpen(false)}
          onSendLater={handleSendLater}
          onSendNow={handleSendNow}
        />
      </Modal>

      <Modal
        isOpen={isResendEmailModalOpen}
        onClose={() => setIsResendEmailModalOpen(false)}
        title="Resend email"
        width="min(95%, 868px)"
        contentClassName="p-0 max-h-[calc(100vh-100px)] overflow-y-auto"
      >
        <SendEmployeeEmailForm
          employeeName={resendEmailMeta.employeeName}
          roleName={resendEmailMeta.roleName}
          initialTo={resendEmailMeta.to}
          initialCc={resendEmailMeta.cc}
          initialBcc={resendEmailMeta.bcc}
          initialSubject={resendEmailMeta.subject}
          initialContent={resendEmailMeta.content}
          onCancel={() => setIsResendEmailModalOpen(false)}
          onSendLater={() => setIsResendEmailModalOpen(false)}
          onSendNow={handleResendSendNow}
          sendLaterLabel="Cancel"
          introText="Would you like to resend the onboarding email now?"
        />
      </Modal>

      <Modal
        isOpen={isResendFailedOpen}
        onClose={() => setIsResendFailedOpen(false)}
        title="Email failed to send"
        width="min(95%, 868px)"
        contentClassName="p-0 max-h-[calc(100vh-100px)] overflow-y-auto"
      >
        {resendFailedData ? (
          <ResendFailedUserOnboardEmailForm
            data={resendFailedData}
            onCancel={() => setIsResendFailedOpen(false)}
            onSubmit={async ({ to, cc, bcc, subject, body }) => {
              if (!emailMeta.userId) return;
              await dispatch(
                // @ts-ignore thunk reuse
                sendClientOnboardEmail({
                  clientId: emailMeta.userId,
                  payload: { action: "send_now", to, cc, bcc, subject, body },
                })
              )
                // @ts-ignore unwrap
                .unwrap()
                .then(() => {
                  setIsResendFailedOpen(false);
                  dispatch(
                    fetchUsers({
                      page: pagination.currentPage,
                      pageSize: pagination.pageSize,
                    })
                  );
                });
            }}
          />
        ) : null}
      </Modal>
    </AppLayout>
  );
};

export default UserManagement;
